// UserList.jsx
import React, { useState, useMemo } from 'react';
import T from '../../../components/common/T';
import { useUsers } from '../../../context/UserContext';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../../components/ConfirmationModal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../../context/AuthContext';
import RoleGate from '../../../components/RoleGate';
import DataTable from '../../../components/common/DataTables.jsx';
import PageLayout, { PageHeader, StatsGrid, Toolbar, FilterPanel, TableCard } from '../../../components/common/PageLayout';
import { TopPrimaryButton } from '../../../components/common/StandardButtons';
import useModuleFilters from '../../../hooks/useModuleFilters';
import {
  TrashIcon, PencilSquareIcon, EyeIcon,
  UsersIcon, CheckCircleIcon, XCircleIcon
} from '@heroicons/react/24/outline';

const UserList = () => {
  const { users = [], deleteUser, fetchUsers } = useUsers();
  const navigate = useNavigate();
  const { checkAccess } = useAuth();

  React.useEffect(() => {
    if (fetchUsers) fetchUsers();
  }, [fetchUsers]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [roleFilter, setRoleFilter] = useState("All");

  const {
    showFilter,
    toggleFilters,
    filterOptions,
    loadingFilters,
  } = useModuleFilters("users", {
    roles: [],
    statuses: [],
  });

  const handleDeleteClick = (id) => { setSelectedId(id); setIsModalOpen(true); };
  const confirmDelete = () => {
    deleteUser(selectedId);
    setIsModalOpen(false);
    toast.success("User deleted successfully!");
  };

  // Hide superadmin users (show all other system and tenant users)
  const visibleUsers = useMemo(() => {
    return users.filter(user => {
      const r = (user.role || user.role_name || '').toLowerCase().trim();
      return r !== 'superadmin' && r !== 'super admin';
    });
  }, [users]);

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = useMemo(() => [
    {
      label: "Total Users",
      value: visibleUsers.length,
      icon: UsersIcon,
      variant: "default",
      isActive: statusFilter === "All" || !statusFilter,
      onClick: () => { setStatusFilter("All"); setRoleFilter("All"); },
    },
    {
      label: "Active",
      value: visibleUsers.filter(u => u.status === 'active').length,
      icon: CheckCircleIcon,
      variant: "green",
      isActive: statusFilter === "active",
      onClick: () => setStatusFilter(s => s === "active" ? "All" : "active"),
    },
    {
      label: "Inactive",
      value: visibleUsers.filter(u => u.status !== 'active').length,
      icon: XCircleIcon,
      variant: "red",
      isActive: statusFilter === "inactive",
      onClick: () => setStatusFilter(s => s === "inactive" ? "All" : "inactive"),
    },
  ], [visibleUsers, statusFilter]);

  const filteredUsers = useMemo(() => {
    let result = [...visibleUsers];
    if (statusFilter && statusFilter !== "All") {
      if (statusFilter === "active") result = result.filter(u => u.status === 'active');
      else result = result.filter(u => u.status !== 'active');
    }
    if (roleFilter && roleFilter !== "All") {
      result = result.filter(u => (u.role || u.role_name || '').toLowerCase() === roleFilter.toLowerCase());
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(u =>
        (u.name || '').toLowerCase().includes(s) ||
        (u.role || '').toLowerCase().includes(s) ||
        (u.email || '').toLowerCase().includes(s)
      );
    }
    return result;
  }, [visibleUsers, statusFilter, roleFilter, searchTerm]);

  const columns = [
    {
      header: "Name", accessor: "name", sortable: true,
      render: (user) => <span className="font-medium text-gray-900 dark:text-white">{user.name}</span>
    },
    {
      header: "Role", accessor: "role", sortable: true,
      render: (user) => <span className="px-3 py-1 bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800/60 rounded-full text-xs font-semibold">{user.role}</span>
    },
    {
      header: "Status", accessor: "status", align: "center", sortable: true,
      render: (user) => (
        <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize border ${user.status === 'active' ? 'bg-green-100 dark:bg-emerald-950/40 text-green-700 dark:text-emerald-400 border-green-200 dark:border-emerald-800/60' : 'bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/60'}`}>
          {user.status}
        </span>
      )
    },
    {
      header: "Actions", align: "right",
      render: (user) => (
        <div className="flex justify-end gap-2">
          {checkAccess('user.view') && (
            <button onClick={() => navigate(`/admin/users/view/${user.id}`)}
              className="p-2 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 rounded hover:bg-blue-100 dark:hover:bg-blue-900/60 transition cursor-pointer" title="View User">
              <EyeIcon className="h-4 w-4" />
            </button>
          )}
          {checkAccess('user.edit') && (
            <button onClick={() => navigate(`/admin/users/edit/${user.id}`)}
              className="p-2 bg-yellow-50 dark:bg-amber-950/40 text-yellow-600 dark:text-amber-400 rounded hover:bg-yellow-100 dark:hover:bg-amber-900/60 transition cursor-pointer" title="Edit User">
              <PencilSquareIcon className="h-4 w-4" />
            </button>
          )}
          {checkAccess('user.delete') && (
            <button onClick={() => handleDeleteClick(user.id)}
              className="p-2 bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 rounded hover:bg-red-100 dark:hover:bg-red-900/60 transition cursor-pointer" title="Delete User">
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <PageLayout>
      <ConfirmationModal
        isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onConfirm={confirmDelete}
        title="Delete User?" message="Are you sure you want to delete this user?"
      />

      {/* Header */}
      <PageHeader
        icon={UsersIcon}
        iconColor="text-[#E8612C]"
        title="Admin Users"
        subtitle="Manage system users and their access"
        onRefresh={fetchUsers}
      >
        <RoleGate permission="user.create">
          <TopPrimaryButton
            onClick={() => navigate('/admin/users/add')}
            label="Add User"
          />
        </RoleGate>
      </PageHeader>

      {/* Stats */}
      <StatsGrid stats={stats} cols={3} />

      {/* Unified Toolbar */}
      <Toolbar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search user name, role, email..."
        tabs={['All', 'active', 'inactive']}
        activeTab={statusFilter}
        onTabChange={setStatusFilter}
        onToggleFilters={toggleFilters}
        showFilters={showFilter}
      />

      {/* Collapsible Filter Panel */}
      <FilterPanel
        isOpen={showFilter}
        loading={loadingFilters}
        onClear={() => { setStatusFilter("All"); setRoleFilter("All"); setSearchTerm(""); }}
      >
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1 flex items-center justify-between">
            <span><T>Role</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
            disabled={loadingFilters}
            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value="All"><T>All Roles</T></option>
            {(filterOptions.roles || []).map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1"><T>User Status</T></label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            disabled={loadingFilters}
            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value="All"><T>All Statuses</T></option>
            <option value="active"><T>Active</T></option>
            <option value="inactive"><T>Inactive</T></option>
          </select>
        </div>
      </FilterPanel>

      {/* Table */}
      <TableCard>
        <DataTable data={filteredUsers} columns={columns} emptyMessage="No users found." />
      </TableCard>
    </PageLayout>
  );
};

export default UserList;